import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// ── Config ───────────────────────────────────────────────────
const BACKEND_URL = "http://das-cloud.com:5000"; // même URL que l'ancien code
const LENGTH = 6;                  // nombre de chiffres
const EXPIRY_SECONDS = 600;        // 10 minutes (comme avant)
const SUCCESS_ROUTE = "/otp-success"; // même route que l'ancien code
const REDIRECT_DELAY = 1800;       // ms d'animation de succès avant redirection

// Position de départ de chaque case (elles "tombent" puis s'alignent)
const FROM = [
  { x: 110, y: -70, r: -28 }, { x: 60, y: 40, r: 22 }, { x: 15, y: -60, r: -14 },
  { x: -15, y: 50, r: 26 }, { x: -60, y: -50, r: -22 }, { x: -110, y: 30, r: 18 },
];
// Confettis du succès
const COLORS = ["#10b981", "#6c5ce7", "#f59e0b", "#ec4899", "#38bdf8"];
const CONFETTI = Array.from({ length: 16 }, (_, i) => {
  const a = (i / 16) * Math.PI * 2;
  const d = 70 + (i % 3) * 22;
  return { dx: Math.cos(a) * d, dy: Math.sin(a) * d, c: COLORS[i % COLORS.length] };
});

const css = `
.otp-page{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f6f7fc;
  font-family:'Segoe UI',system-ui,sans-serif;position:relative;overflow:hidden;padding:16px}
.otp-light{position:fixed;width:460px;height:460px;border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(124,108,240,.20),transparent 65%);
  transform:translate(-50%,-50%);left:50%;top:40%;transition:left .3s ease-out,top .3s ease-out}
.otp-card{position:relative;width:100%;max-width:420px;min-height:400px;background:#fff;border-radius:28px;
  padding:44px 32px 32px;text-align:center;border:1.5px solid transparent;
  box-shadow:0 20px 60px rgba(60,50,140,.12);transition:border-color .5s,box-shadow .5s}
.otp-card.success{border-color:#6ee7b7;box-shadow:0 0 0 8px rgba(16,185,129,.08),0 20px 70px rgba(16,185,129,.22)}
.otp-card.shake{animation:otpShake .4s}
.otp-card h1{margin:0 0 8px;font-size:1.55rem;font-weight:800;color:#1b1b3a}
.otp-sub{margin:0;color:#7b7f99;font-size:.88rem;line-height:1.6}
.otp-sub b{color:#6c5ce7}
.otp-row{display:flex;justify-content:center;gap:10px;margin:34px 0 18px}
.otp-box{width:48px;height:58px;border-radius:14px;border:1.5px solid #cfcbf2;background:#fff;
  text-align:center;font-size:1.4rem;font-weight:700;color:#1b1b3a;outline:none;padding:0;
  animation:otpDrop .8s cubic-bezier(.2,1.25,.35,1) backwards;animation-delay:calc(var(--i) * 70ms);
  transition:border-color .2s,box-shadow .2s,background .2s}
.otp-box:focus{border-color:#6c5ce7;box-shadow:0 0 0 4px rgba(108,92,231,.16)}
.otp-box.filled{border-color:#6c5ce7;background:#f6f4ff}
.otp-card.error .otp-box{border-color:#e5484d}
.otp-timer{font-size:.8rem;color:#7b7f99;margin:0 0 6px}
.otp-timer b{color:#6c5ce7}
.otp-msg{min-height:20px;margin:0 0 12px;font-size:.82rem;color:#e5484d}
.otp-btn{width:100%;padding:14px;border:none;border-radius:14px;background:#6c5ce7;color:#fff;
  font-size:.92rem;font-weight:700;cursor:pointer;font-family:inherit;transition:transform .15s,box-shadow .15s,opacity .2s}
.otp-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 20px rgba(108,92,231,.35)}
.otp-btn:disabled{opacity:.5;cursor:default}
.otp-btn:focus-visible,.otp-link:focus-visible{outline:3px solid rgba(108,92,231,.4);outline-offset:3px}
.otp-link{margin-top:16px;background:none;border:none;color:#6c5ce7;font-weight:600;font-size:.85rem;cursor:pointer;font-family:inherit}
.otp-link:disabled{color:#b9b1f7;cursor:default}
.otp-ok{position:relative;height:150px;margin-top:34px;display:flex;align-items:center;justify-content:center}
.otp-check{width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);
  color:#fff;font-size:2rem;display:flex;align-items:center;justify-content:center;
  box-shadow:0 0 0 10px rgba(16,185,129,.14),0 10px 30px rgba(16,185,129,.45);animation:otpPop .6s cubic-bezier(.2,1.4,.4,1)}
.otp-conf{position:absolute;left:50%;top:50%;width:8px;height:8px;border-radius:2px;background:var(--c);
  animation:otpBurst 1.1s ease-out both}
@keyframes otpDrop{from{transform:translate(var(--fx),var(--fy)) rotate(var(--fr));opacity:0}
  to{transform:none;opacity:1}}
@keyframes otpPop{from{transform:scale(.3);opacity:0}to{transform:scale(1);opacity:1}}
@keyframes otpBurst{from{transform:translate(-50%,-50%) scale(.4);opacity:1}
  to{transform:translate(calc(-50% + var(--dx)),calc(-50% + var(--dy))) rotate(200deg) scale(1);opacity:0}}
@keyframes otpShake{25%{transform:translateX(-8px)}75%{transform:translateX(8px)}}
@media (max-width:420px){.otp-box{width:42px;height:52px}.otp-row{gap:7px}}
@media (prefers-reduced-motion:reduce){.otp-box,.otp-check,.otp-conf,.otp-card.shake{animation:none}.otp-light{transition:none}}
`;

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function OtpVerify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";

  const [digits, setDigits] = useState(Array(LENGTH).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [left, setLeft] = useState(EXPIRY_SECONDS);
  const refs = useRef([]);
  const lightRef = useRef(null);

  // Lumière qui suit la souris
  useEffect(() => {
    const move = (e) => {
      if (!lightRef.current) return;
      lightRef.current.style.left = e.clientX + "px";
      lightRef.current.style.top = e.clientY + "px";
    };
    window.addEventListener("pointermove", move);
    return () => window.removeEventListener("pointermove", move);
  }, []);

  // Compte à rebours
  useEffect(() => {
    if (left <= 0 || success) return;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, success]);

  // Focus après l'animation d'entrée
  useEffect(() => {
    const t = setTimeout(() => refs.current[0]?.focus(), 700);
    return () => clearTimeout(t);
  }, []);

  const headers = { "Content-Type": "application/json" };

  const verify = async (code) => {
    if (loading || left <= 0) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, otp: code }), // adaptez les noms de champs si besoin
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || "Code incorrect");

      setSuccess(true);
      setTimeout(() => navigate(SUCCESS_ROUTE), REDIRECT_DELAY);
    } catch (e) {
      setError(e.message);
      setShake(true);
      setTimeout(() => setShake(false), 400);
      setDigits(Array(LENGTH).fill(""));
      refs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const onChange = (i, e) => {
    const v = e.target.value.replace(/\D/g, "").slice(-1);
    if (!v) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (i < LENGTH - 1) refs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) next[i] = "";
      else if (i > 0) { next[i - 1] = ""; refs.current[i - 1]?.focus(); }
      setDigits(next);
    } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < LENGTH - 1) refs.current[i + 1]?.focus();
    else if (e.key === "Enter" && digits.every(Boolean)) verify(digits.join(""));
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(LENGTH).fill("");
    text.split("").forEach((c, k) => (next[k] = c));
    setDigits(next);
    refs.current[Math.min(text.length, LENGTH - 1)]?.focus();
  };

  const resend = async () => {
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/resend-otp`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Échec de l'envoi du code");
      setDigits(Array(LENGTH).fill(""));
      setLeft(EXPIRY_SECONDS);
      refs.current[0]?.focus();
    } catch (e) {
      setError(e.message);
    }
  };

  const full = digits.every(Boolean);

  return (
    <div className="otp-page">
      <style>{css}</style>
      <div className="otp-light" ref={lightRef} />

      <main className={`otp-card${success ? " success" : ""}${shake ? " shake" : ""}${error ? " error" : ""}`}>
        {success ? (
          <>
            <h1>Email vérifié</h1>
            <p className="otp-sub">Votre code est correct. Redirection en cours…</p>
            <div className="otp-ok">
              <div className="otp-check">✓</div>
              {CONFETTI.map((c, i) => (
                <span key={i} className="otp-conf" style={{ "--dx": `${c.dx}px`, "--dy": `${c.dy}px`, "--c": c.c }} />
              ))}
            </div>
          </>
        ) : (
          <>
            <h1>Vérification email</h1>
            <p className="otp-sub">
              Un code à {LENGTH} chiffres a été envoyé à<br />
              <b>{email}</b>
            </p>

            <div className="otp-row" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (refs.current[i] = el)}
                  className={`otp-box${d ? " filled" : ""}`}
                  style={{
                    "--i": i,
                    "--fx": `${FROM[i % FROM.length].x}px`,
                    "--fy": `${FROM[i % FROM.length].y}px`,
                    "--fr": `${FROM[i % FROM.length].r}deg`,
                  }}
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`Chiffre ${i + 1}`}
                  value={d}
                  disabled={loading}
                  onChange={(e) => onChange(i, e)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>

            <p className="otp-timer">
              {left > 0 ? <>Code expire dans <b>{fmt(left)}</b></> : "Code expiré. Demandez-en un nouveau."}
            </p>
            <div className="otp-msg" role="alert">{error}</div>

            <button className="otp-btn" disabled={!full || loading || left <= 0} onClick={() => verify(digits.join(""))}>
              {loading ? "Vérification…" : "Vérifier le code"}
            </button>
            <button className="otp-link" onClick={resend} disabled={loading}>
              Renvoyer un nouveau code
            </button>
          </>
        )}
      </main>
    </div>
  );
}